/**
 * JWT utility functions for token validation
 */

interface JWTPayload {
  exp?: number
  iat?: number
  [key: string]: unknown
}

/**
 * Decode a JWT token without verification
 * Returns null if token is invalid or cannot be decoded
 */
function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    // Decode the payload (second part)
    const payload = parts[1]
    // Replace URL-safe base64 characters
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    // Add padding if needed
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    
    const decoded = atob(padded)
    return JSON.parse(decoded) as JWTPayload
  } catch (error) {
    console.error('Failed to decode JWT:', error)
    return null
  }
}

/**
 * Check if a JWT token is expired
 * Returns true if token is expired or invalid, false if valid
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true
  }

  const payload = decodeJWT(token)
  if (!payload) {
    return true
  }
  // App session JWTs from Apex always include ``exp``; if absent, do not treat as expired
  // (avoids clearing a valid session when claims differ slightly).
  if (payload.exp === undefined || payload.exp === null) {
    return false
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000
  const currentTime = Date.now()

  // Add a small buffer (5 seconds) to account for clock skew
  return currentTime >= expirationTime - 5000
}

/**
 * Get the expiration time of a JWT token
 * Returns null if token is invalid or has no expiration
 */
export function getTokenExpiration(token: string | null): Date | null {
  if (!token) {
    return null
  }

  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return null
  }

  return new Date(payload.exp * 1000)
}

/**
 * Get the remaining time until token expiration in milliseconds
 * Returns null if token is invalid or expired
 */
export function getTokenTimeRemaining(token: string | null): number | null {
  if (!token) {
    return null
  }

  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return null
  }

  const expirationTime = payload.exp * 1000
  const currentTime = Date.now()
  const remaining = expirationTime - currentTime

  return remaining > 0 ? remaining : null
}
